import React, { useState, useEffect, useRef } from 'react';
import { getGeneratorData, generateRecipe } from '../api/apiServices';

interface Ingredient {
  name: string;
  type: string;
  category: string;
}

interface Format {
  name: string;
  note: string;
}

interface Cuisine {
  name: string;
  note: string;
}

interface Allergy {
  name: string;
  note: string;
}

interface Intolerance {
  name: string;
  note: string;
}

const RecipeGenerator: React.FC = () => {
  const [recipe, setRecipe] = useState<string | null>(null);
  const [activeSearch, setActiveSearch] = useState<string | null>(null);
  const [searchQueries, setSearchQueries] = useState({
    cuisine: '',
    format: '',
    allergy: '',
    intolerance: '',
    meat: '',
    fish: '',
    vegetableProteins: ''
  });
  const [selectedValues, setSelectedValues] = useState({
    cuisine: '',
    format: '',
    allergy: '',
    intolerance: '',
    meat: '',
    fish: '',
    vegetableProteins: ''
  });
  const [cuisines, setCuisines] = useState<Cuisine[]>([]);
  const [formats, setFormats] = useState<Format[]>([]);
  const [meats, setMeats] = useState<Ingredient[]>([]);
  const [fish, setFish] = useState<Ingredient[]>([]);
  const [vegetableProteins, setVegetableProteins] = useState<Ingredient[]>([]);
  const [allergies, setAllergies] = useState<Allergy[]>([]);
  const [intolerances, setIntolerances] = useState<Intolerance[]>([]);

  const containerRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    const loadGeneratorData = async () => {
      try {
        const data = await getGeneratorData();
        setMeats(data.ingredients.filter((ingredient: Ingredient) => ingredient.category === 'Meat'));
        setFish(data.ingredients.filter((ingredient: Ingredient) => ingredient.category === 'Seafood'));
        setVegetableProteins(data.ingredients.filter((ingredient: Ingredient) => ingredient.category === 'Vegetable Proteins'));
        setAllergies(data.allergies);
        setIntolerances(data.intolerances);
        setCuisines(data.cuisine);
        setFormats(data.format);
      } catch (error) {
        console.error('Error fetching generator data:', error);
      }
    };

    loadGeneratorData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isClickInside = Object.values(containerRefs.current).some(
        (ref) => ref && ref.contains(target)
      );

      if (!isClickInside) {
        setActiveSearch(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSearchChange = (type: keyof typeof searchQueries, value: string) => {
    setSearchQueries((prev) => ({ ...prev, [type]: value }));
  };

  const handleSelectValue = (type: keyof typeof searchQueries, value: string) => {
    setSelectedValues((prev) => ({ ...prev, [type]: value }));
    setSearchQueries((prev) => ({ ...prev, [type]: '' }));
    setActiveSearch(null);
  };

  const handleSearchFocus = (type: keyof typeof searchQueries) => {
    setActiveSearch(type);
  };

  const handleRemoveSelectedValue = (type: keyof typeof searchQueries) => {
    setSelectedValues((prev) => ({ ...prev, [type]: '' }));
    setActiveSearch(type);
  };

  const filteredData = (data: any[], query: string) =>
    data.filter(item => item.name.toLowerCase().includes(query.toLowerCase()));

  const renderInput = (label: string, type: keyof typeof searchQueries, data: any[], selectedValue: string) => {
    const query = searchQueries[type];
    const filtered = filteredData(data, query);

    return (
      <div
        ref={(el) => (containerRefs.current[type] = el)}
        className={`${type}-input-container relative`}
      >
        <div className="flex items-center">
          <input
            type="text"
            value={selectedValue || query}
            onChange={(e) => handleSearchChange(type, e.target.value)}
            onFocus={() => handleSearchFocus(type)}
            disabled={!!selectedValue}
            placeholder={selectedValue ? `Selected: ${selectedValue}` : `Search ${label}`}
            className={`border rounded p-2 w-full ${selectedValue ? 'bg-gray-100' : ''}`}
          />
          {selectedValue && (
            <button
              onClick={() => handleRemoveSelectedValue(type)}
              className="ml-2 text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          )}
        </div>
        {activeSearch === type && !selectedValue && (
          <div
            className="absolute top-full left-0 right-0 bg-white border max-h-40 overflow-y-auto z-50 shadow-lg"
          >
            {filtered.length > 0 ? (
              filtered.map(item => (
                <div
                  key={item.name}
                  onClick={() => handleSelectValue(type, item.name)}
                  className="p-2 hover:bg-gray-200 cursor-pointer"
                >
                  {item.name}
                </div>
              ))
            ) : (
              <div className="p-2">No results found</div>
            )}
          </div>
        )}
      </div>
    );
  };

  const handleGenerateRecipe = async () => {
    if (!selectedValues.cuisine || !selectedValues.format) {
      alert('Please select a cuisine and format before generating a recipe.');
      return;
    }
    try {
      const recipe = await generateRecipe(selectedValues);
      setRecipe(recipe.title); // Assuming recipe has a title
    } catch (error) {
      console.error('Error generating recipe:', error);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white shadow-md rounded-lg space-y-6">
      <h2 className="text-2xl font-bold mb-4">Recipe Generator</h2>
      <ul>
        <li className="my-2">{renderInput('Cuisine', 'cuisine', cuisines, selectedValues.cuisine)}</li>
        <li className="my-2">{renderInput('Format', 'format', formats, selectedValues.format)}</li>
        <li className="my-2">{renderInput('Allergies', 'allergy', allergies, selectedValues.allergy)}</li>
        <li className="my-2">{renderInput('Intolerances', 'intolerance', intolerances, selectedValues.intolerance)}</li>
        <li className="my-2">{renderInput('Meat', 'meat', meats, selectedValues.meat)}</li>
        <li className="my-2">{renderInput('Fish', 'fish', fish, selectedValues.fish)}</li>
        <li className="my-2">{renderInput('Vegetable Proteins', 'vegetableProteins', vegetableProteins, selectedValues.vegetableProteins)}</li>
      </ul>
      <button
        onClick={handleGenerateRecipe}
        className="w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700"
      >
        Generate Recipe
      </button>
      {recipe && (
        <div className="p-4 bg-gray-100 rounded-lg">
          <h3 className="text-lg font-bold">Generated Recipe:</h3>
          <p>{recipe}</p>
        </div>
      )}
    </div>
  );
};

export default RecipeGenerator;
